import { useEffect, useState } from 'react';

import { Workbox } from './Workbox';
import {
  DevHubBox,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubLink,
  devhubMakeStyles,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import dxLogo from '../../app/icons/dx-logo.png';
import accessLogo from '../../app/icons/request-access.svg';
import searchLogo from '../../app/icons/search.svg';
import queryFabricLogo from '../../app/icons/query-fabric.svg';
import dataPipelineLogo from '../../app/icons/data-pipelines.svg';
import devxLogo from '../../app/icons/devx-logo-light.svg';
import elasticLogo from '../../app/icons/elasticsearch.svg';
import fusionLogo from '../../app/icons/fusion-logo.png';
import oneCloudLogo from '../../app/icons/one-cloud-logo.png';
import diagramLogo from '../../app/icons/diagrams-logo.png';
import devxEnableLogo from '../../app/icons/devxenablefavicon.png';
import { ConfigFeatureCheck } from '../../util/ConfigFeatureCheck';
import { Feature } from '@devxdiscover/devhub-common';
import { useGlobalPolicyAccess } from '../../util/useGlobalPolicyAccess';
import useGoogleAnalytics from '../../util/useGoogleAnalytics';
import { GA4_EVENTS } from '../../util/analyticsEvents';

const useStyles = devhubMakeStyles({
  dxImage: {
    height: '2rem',
    marginRight: '.5rem',
  },
  shieldImage: {
    height: '1.5rem',
    marginRight: '.5rem',
  },
  cloudImage: {
    height: '2rem',
    marginRight: '.5rem',
  },
  image: {
    height: '2rem',
  },
});

const SummaryTable = () => {
  const classes = useStyles();
  const [showMore, setShowMore] = useState(false);
  const [showMoreMessage, setShowMoreMessage] = useState('');
  const { hasAccess: hasCGAReportAccess, loading: cgaAccessLoading } =
    useGlobalPolicyAccess('AccessCGAHierarchyReportPolicy');
  const { hasAccess: hasDETPortalAccess, loading: detAccessLoading } =
    useGlobalPolicyAccess('AccessDETAdminPagePolicy');
  const { sendEvent } = useGoogleAnalytics();

  useEffect(() => {
    setShowMore(false);
    setShowMoreMessage('View All');
  }, []);

  const title = 'My Tools';

  return (
    <Workbox
      consistentHeight
      color="orange"
      title={title}
      component="tools"
      buttonTitle={showMoreMessage}
      showButton={showMore}
      subHeader="Quick access to your tools and resources"
    >
      <>
        {/* DX Tools */}
        <DevHubGrid backgroundColor="workbox">
          <DevHubBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            margin="medium"
            padding="xsmall"
          >
            <img className={classes.dxImage} src={dxLogo} alt="dx Tools" />
            <DevHubTypography>DX TOOLS</DevHubTypography>
          </DevHubBox>
        </DevHubGrid>

        <DevHubGrid container>
          <DevHubGrid item xs={3}>
            <DevHubBox display="flex" justifyContent="center">
              <DevHubTooltip title="Search metadata and explore details including data dictionaries, lineage, and more.">
                <DevHubLink
                  target="_blank"
                  href="https://dx.comcast.com/#/access-control-manager/platform"
                >
                  <img
                    className={classes.image}
                    src={accessLogo}
                    alt="Access Manager"
                  />
                </DevHubLink>
              </DevHubTooltip>
            </DevHubBox>
            <DevHubBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <DevHubTypography>Access</DevHubTypography>
              <DevHubTypography>Manager</DevHubTypography>
            </DevHubBox>
          </DevHubGrid>
          <DevHubGrid item xs={3}>
            <DevHubBox display="flex" justifyContent="center">
              <DevHubTooltip title="Search metadata and explore details including data dictionaries, lineage, and more.">
                <DevHubLink
                  target="_blank"
                  href="https://dx.comcast.com/#/metadata"
                >
                  <img
                    className={classes.image}
                    src={searchLogo}
                    alt="Search"
                  />
                </DevHubLink>
              </DevHubTooltip>
            </DevHubBox>
            <DevHubBox display="flex" justifyContent="center">
              <DevHubTypography>Search</DevHubTypography>
            </DevHubBox>
          </DevHubGrid>
          <DevHubGrid item xs={3}>
            <DevHubBox display="flex" justifyContent="center">
              <DevHubTooltip title="Access data across all dx Data Foundry platforms for reporting and analytics.">
                <DevHubLink
                  target="_blank"
                  href="https://dx.comcast.com/#/query-fabric"
                >
                  <img
                    className={classes.image}
                    src={queryFabricLogo}
                    alt="Query Fabric"
                  />
                </DevHubLink>
              </DevHubTooltip>
            </DevHubBox>
            <DevHubBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <DevHubTypography>Query</DevHubTypography>
              <DevHubTypography>Fabric</DevHubTypography>
            </DevHubBox>
          </DevHubGrid>
          <DevHubGrid item xs={3}>
            <DevHubBox display="flex" justifyContent="center">
              <DevHubTooltip title="Automate the ingestion and transformation of data into the dx Data Foundry.">
                <DevHubLink
                  target="_blank"
                  href="https://dx.comcast.com/#/pipeline"
                >
                  <img
                    className={classes.image}
                    src={dataPipelineLogo}
                    alt="Data Pipelines"
                  />
                </DevHubLink>
              </DevHubTooltip>
            </DevHubBox>
            <DevHubBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <DevHubTypography>Data</DevHubTypography>
              <DevHubTypography>Pipelines</DevHubTypography>
            </DevHubBox>
          </DevHubGrid>
        </DevHubGrid>

        {/* DevX Tools */}
        <DevHubGrid backgroundColor="workbox">
          <DevHubBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            margin="medium"
            padding="small"
          >
            <img
              className={classes.shieldImage}
              src={devxLogo}
              alt="DevX Tools"
            />
            <DevHubTypography>DEVX TOOLS</DevHubTypography>
          </DevHubBox>
        </DevHubGrid>

        <DevHubGrid container>
          <DevHubGrid item xs={4}>
            <DevHubBox display="flex" justifyContent="center">
              <DevHubTooltip title="Fusion is a federated GraphQL service providing a convenient entry point for querying data across a multitude of data sources.">
                <DevHubLink target="_blank" href="https://fusion.comcast.net/">
                  <img
                    className={classes.image}
                    src={fusionLogo}
                    alt="Fusion"
                  />
                </DevHubLink>
              </DevHubTooltip>
            </DevHubBox>
            <DevHubBox display="flex" justifyContent="center">
              <DevHubTypography>Fusion</DevHubTypography>
            </DevHubBox>
          </DevHubGrid>
          {!cgaAccessLoading && hasCGAReportAccess && (
            <ConfigFeatureCheck feature={Feature.CGA_HIERARCHY_REPORT}>
              <DevHubGrid item xs={4}>
                <DevHubBox display="flex" justifyContent="center">
                  <DevHubTooltip title="CGA hierarchy reporting.">
                    <DevHubLink
                      href="/cga-hierarchy-report"
                      onClick={() =>
                        sendEvent({
                          action: GA4_EVENTS.MY_TOOLS_CGA_REPORT_CLICKED,
                          subject: 'CGA Hierarchy Report Clicked',
                        })
                      }
                    >
                      <DevHubDynamicIcon
                        fontSize="large"
                        color="primary"
                        iconName="Assignment"
                      />
                    </DevHubLink>
                  </DevHubTooltip>
                </DevHubBox>
                <DevHubBox display="flex" justifyContent="center">
                  <DevHubTypography>CGA Report</DevHubTypography>
                </DevHubBox>
              </DevHubGrid>
            </ConfigFeatureCheck>
          )}
          <DevHubGrid item xs={4}>
            <DevHubBox display="flex" justifyContent="center">
              <DevHubTooltip title="Database provisioning service.">
                <DevHubLink href="/marketplace/templates/v2/default/c32fe4b656abb5dc4121f59763670846e088fbb2">
                  <DevHubDynamicIcon
                    fontSize="large"
                    color="primary"
                    iconName="FormatListBulleted"
                  />
                </DevHubLink>
              </DevHubTooltip>
            </DevHubBox>
            <DevHubBox display="flex" justifyContent="center">
              <DevHubTypography>DBaaS</DevHubTypography>
            </DevHubBox>
          </DevHubGrid>
          <DevHubGrid item xs={4}>
            <DevHubBox display="flex" justifyContent="center">
              <DevHubTooltip title="Database provisioning service.">
                <DevHubLink
                  target="_blank"
                  href="https://federated-us.eaas.comcast.net/"
                >
                  <img className={classes.image} src={elasticLogo} alt="DaaS" />
                </DevHubLink>
              </DevHubTooltip>
            </DevHubBox>
            <DevHubBox display="flex" justifyContent="center">
              <DevHubTypography>DaaS Logging</DevHubTypography>
            </DevHubBox>
          </DevHubGrid>
          {!detAccessLoading && hasDETPortalAccess && (
            <DevHubGrid item xs={4}>
              <DevHubBox display="flex" justifyContent="center">
                <DevHubTooltip title="DET Portal for admin management.">
                  <DevHubLink href="/det-portal">
                    <img
                      className={classes.image}
                      src={devxEnableLogo}
                      alt="DET Portal"
                    />
                  </DevHubLink>
                </DevHubTooltip>
              </DevHubBox>
              <DevHubBox display="flex" justifyContent="center">
                <DevHubTypography>DET Portal</DevHubTypography>
              </DevHubBox>
            </DevHubGrid>
          )}
        </DevHubGrid>

        {/* Other Tools */}
        <DevHubGrid backgroundColor="workbox">
          <DevHubBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            margin="medium"
            padding="small"
          >
            <DevHubDynamicIcon iconName="LibraryBooks" />
            <DevHubTypography>&nbsp; OTHER TOOLS</DevHubTypography>
          </DevHubBox>
        </DevHubGrid>

        <DevHubGrid container>
          <DevHubGrid item xs={6}>
            <DevHubBox display="flex" justifyContent="center">
              <DevHubTooltip title="OneCloud Portal">
                <DevHubLink
                  target="_blank"
                  href="https://onecloud.comcast.net/"
                >
                  <img
                    className={classes.cloudImage}
                    src={oneCloudLogo}
                    alt="OneCloud Portal"
                  />
                </DevHubLink>
              </DevHubTooltip>
            </DevHubBox>
            <DevHubBox display="flex" justifyContent="center">
              <DevHubTypography>OneCloud Portal</DevHubTypography>
            </DevHubBox>
          </DevHubGrid>
          <DevHubGrid item xs={6}>
            <DevHubBox display="flex" justifyContent="center">
              <DevHubTooltip title="Diagram">
                <DevHubLink target="_blank" href="https://diagram.comcast.com/">
                  <img
                    className={classes.image}
                    src={diagramLogo}
                    alt="Diagram"
                  />
                </DevHubLink>
              </DevHubTooltip>
            </DevHubBox>
            <DevHubBox display="flex" justifyContent="center">
              <DevHubTypography>Diagram</DevHubTypography>
            </DevHubBox>
          </DevHubGrid>
        </DevHubGrid>
      </>
    </Workbox>
  );
};

/**
 * A component that displays a summary of incidents and other notable activities for authenticated user.
 * @constructor
 */
export const MyTools = () => {
  return <SummaryTable />;
};
